/**
 * The authenticated planner session — G3.2 / Keycloak, `docs/adr/0043`.
 *
 * `src/features/launch/identity.ts` hardcodes `PLANNER_ACTOR = null` because, in its own
 * words, "there is no login path in `src/app/`, no identity endpoint in the pinned
 * client, and no token on the transport." Keycloak now runs in production — realm
 * `bata`, public client `bata-ui`, PKCE required — and this module is the login path and
 * the token store that comment was waiting on. It exposes exactly the surface named for
 * it (`useAuthSession`, `login`, `logout`, `handleAuthCallback`, `getAccessToken`) and
 * nothing else: wiring `PLANNER_ACTOR` from `useAuthSession().actor` and attaching a
 * bearer header in `src/api/http.ts` from `getAccessToken()` are different agents' changes,
 * made against this shape without seeing this file's internals.
 *
 * ── Where the tokens live, and why ────────────────────────────────────────────
 *
 * Only the REFRESH token is persisted, and only in `sessionStorage`:
 *
 *   * `sessionStorage` over `localStorage` — a refresh token is a long-lived bearer
 *     credential. Anything that can run JavaScript on this origin (an XSS, a compromised
 *     dependency) can read either storage, so persistence here is not a security boundary
 *     either way; the choice is about the SIZE of the exposure window. `localStorage`
 *     keeps the credential alive across tabs closed weeks ago and across browser
 *     restarts. `sessionStorage` dies with the tab. What's given up is convenience — a
 *     second tab opened by the same planner cannot inherit the session, and has to log in
 *     again — for a materially smaller persistence footprint on the credential that
 *     matters most. G2 doesn't name this specific tradeoff, so it's written here.
 *   * the ACCESS token is never persisted anywhere, in-memory only. It's the credential
 *     actually presented on every API call, so keeping it off both storages bounds its
 *     exposure to "this tab, this page load"; a reload re-derives a fresh one from the
 *     refresh token rather than resurrecting an old one from disk.
 *
 * Consequence: every page load that finds a stored refresh token silently re-runs the
 * refresh grant before the session counts as authenticated again — that round trip is
 * exactly what `isLoading` on `AuthSession` reports.
 */

import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { DEFAULT_BASE_URL, newIdempotencyKey } from '../api/http';
import {
  buildAuthorizeUrl,
  buildEndSessionUrl,
  exchangeCodeForTokens,
  generateCodeChallenge,
  generateCodeVerifier,
  asKeycloakAction,
  refreshTokens,
  type KeycloakAction,
} from './oidc';

/** The public shape other agents type against. Do not add fields here without a G4-style
 *  reason — `useAuthSession()` is the one thing wired into route chrome without a second
 *  look at this file. */
export interface AuthSession {
  readonly actor: string | null;
  /**
   * The planner's given name, for a greeting — audit v5 N-07. `null` when the token carries
   * none, and then a greeting names nobody: `actor` is a LOGIN (`e2e-fixture`, `mrossi01`, an
   * email), and a login set as a greeting reads as a code, not as a person.
   */
  readonly givenName: string | null;
  /** The planner's full name, for the identity in the rail's foot — audit v5b R-08. */
  readonly displayName: string | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
}

const redirectUri = (): string => `${window.location.origin}/auth/callback`;

/** Keys for the one-shot PKCE handshake stashed between `login()` and the redirect back
 *  to `/auth/callback`. Deliberately NOT part of the zustand store below — they exist for
 *  the lifetime of one redirect round trip and are erased by `handleAuthCallback`, unlike
 *  the refresh token, which is meant to outlive a reload. */
const HANDSHAKE_STATE_KEY = 'bata.auth.handshake.state';
const HANDSHAKE_VERIFIER_KEY = 'bata.auth.handshake.verifier';
const HANDSHAKE_PATH_KEY = 'bata.auth.handshake.path';
/** Which application-initiated action this round trip started, if any — so the callback can
 *  say what happened in the words of THAT action (a password, or personal data). */
const HANDSHAKE_ACTION_KEY = 'bata.auth.handshake.action';

/** How long before `expires_in` elapses the silent refresh fires. Keycloak's default
 *  access-token lifetime is 5 minutes; 60s of headroom comfortably survives request
 *  latency and a slow tab (backgrounded, throttled timers) without refreshing so early
 *  that a short-lived token gets churned for no reason. */
const REFRESH_SKEW_MS = 60_000;

/** Decodes a JWT's claims without verifying its signature. That's correct here: Keycloak
 *  is the one that produced this token over TLS moments ago, and the ONLY thing read off
 *  it is a display name for the corner of the screen — the actor the platform enforces
 *  is whatever the bearer token resolves to server-side (G3.2), not whatever this client
 *  claims to have decoded. Reused for both the ID token (login) and the access token
 *  (silent refresh, which per RFC 6749 doesn't return a new ID token) because Keycloak's
 *  `bata-ui` client maps `preferred_username` onto both by default. */
interface DisplayClaims {
  readonly sub: string | null;
  readonly preferredUsername: string | null;
  readonly givenName: string | null;
  readonly name: string | null;
}

const NO_CLAIMS: DisplayClaims = { sub: null, preferredUsername: null, givenName: null, name: null };

/** A claim worth showing: a non-blank string, trimmed. Anything else is absent. */
function textClaim(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * `given_name` and `name` are OIDC's standard `profile` claims. `login()` asks for the
 * `profile` scope (`oidc.ts`) and the realm's `bata-ui` client keeps Keycloak's default
 * scopes, whose `profile` mappers write both into the access token from the user's first and
 * last name — every user in `bata-infra`'s realm export carries a `firstName`.
 */
export function decodeJwtClaims(token: string): DisplayClaims {
  const payload = token.split('.')[1];
  if (!payload) return NO_CLAIMS;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as Record<string, unknown>;
    return {
      sub: textClaim(claims['sub']),
      preferredUsername: textClaim(claims['preferred_username']),
      givenName: textClaim(claims['given_name']),
      name: textClaim(claims['name']),
    };
  } catch {
    // A token this client cannot parse is not a reason to crash the app over a display
    // name — `actor` degrades to null and `isAuthenticated` still reflects a real session.
    return NO_CLAIMS;
  }
}

interface Tokens {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
}

interface SessionStore {
  readonly accessToken: string | null;
  readonly idToken: string | null;
  readonly refreshToken: string | null;
  readonly actor: string | null;
  readonly givenName: string | null;
  readonly displayName: string | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  applyTokens: (tokens: Tokens, idToken?: string) => void;
  clear: () => void;
  setLoading: (isLoading: boolean) => void;
}

/** `sessionStorage` guarded the way `theme.ts` guards `document` — this module is
 *  imported by code that also runs under Vitest/jsdom and, transitively, anywhere
 *  `window` isn't the browser window. */
function safeSessionStorage(): StateStorage {
  if (typeof window === 'undefined') {
    return { getItem: () => null, setItem: () => {}, removeItem: () => {} };
  }
  return window.sessionStorage;
}

/**
 * Bridges this tab's bearer token into the short-lived, HttpOnly cookie
 * `bata-platform`'s `POST /internal/session` mints (`services/api/routers/internal.py`
 * there explains the gap this closes): a plain top-level browser navigation to
 * Grafana or Dagster never carries this tab's access token — it lives here, in
 * memory, and a full-page navigation never runs this module's JS before the
 * request leaves the browser. The cookie is the one credential that can reach
 * Caddy's `forward_auth` check on that kind of navigation.
 *
 * Fire-and-forget, deliberately. Called from every `applyTokens` — fresh login
 * and every silent refresh alike, so the cookie stays fresh for as long as the
 * planner's session does — and a planner who never opens Grafana pays nothing
 * beyond one extra request per refresh cycle. A failure here (network blip, the
 * API briefly unreachable, a non-2xx response) must never block or fail the
 * login/refresh it rides on: Grafana being briefly unreachable is a far smaller
 * problem than the whole app failing to authenticate because this plumbing call
 * didn't 2xx. Wrapped in its own try/catch, not just a `.catch()` on the
 * returned promise: `fetch()` itself can throw synchronously (an invalid header
 * value, per the Fetch spec) before it ever returns a promise to attach one to.
 */
function mintInternalSession(accessToken: string): void {
  try {
    void fetch(`${DEFAULT_BASE_URL}/api/v1/internal/session`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Idempotency-Key': newIdempotencyKey(),
      },
    })
      .then((response) => {
        if (!response.ok) {
          console.error(`bata-ui: internal-session mint answered ${response.status}.`);
        }
      })
      .catch((error: unknown) => {
        console.error('bata-ui: could not mint the internal-surface session cookie.', error);
      });
  } catch (error: unknown) {
    console.error('bata-ui: could not mint the internal-surface session cookie.', error);
  }
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function cancelScheduledRefresh(): void {
  if (refreshTimer !== null) clearTimeout(refreshTimer);
  refreshTimer = null;
}

function scheduleRefresh(expiresIn: number): void {
  cancelScheduledRefresh();
  const delay = Math.max(expiresIn * 1000 - REFRESH_SKEW_MS, 0);
  refreshTimer = setTimeout(() => { void runSilentRefresh(); }, delay);
}

async function runSilentRefresh(): Promise<void> {
  const current = useAuthSessionStore.getState().refreshToken;
  if (!current) return;
  try {
    const tokens = await refreshTokens(current);
    useAuthSessionStore.getState().applyTokens(tokens);
  } catch (error) {
    // Never swallow it: a failed silent refresh means the session is over (the refresh
    // token expired, was revoked, or Keycloak is unreachable), and pretending otherwise
    // would leave `isAuthenticated: true` pointed at a dead access token. The planner
    // sees a normal logged-out state and can log in again; nothing about the failure
    // itself is presented, since there's no surface here that shows one.
    console.error('bata-ui: silent token refresh failed; ending session.', error);
    useAuthSessionStore.getState().clear();
  }
}

export const useAuthSessionStore = create<SessionStore>()(
  persist(
    (set) => ({
      accessToken: null,
      idToken: null,
      refreshToken: null,
      actor: null,
      givenName: null,
      displayName: null,
      isAuthenticated: false,
      // True until the module-init restore (see `onRehydrateStorage` below) resolves one
      // way or the other. A build with no stored refresh token clears this synchronously
      // right after rehydration; a build with one clears it when the restore settles.
      isLoading: true,
      applyTokens: (tokens, idToken) => {
        const claims = decodeJwtClaims(tokens.accessToken);
        set((state) => ({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          idToken: idToken ?? state.idToken,
          actor: claims.preferredUsername ?? claims.sub,
          givenName: claims.givenName,
          displayName: claims.name ?? claims.givenName,
          isAuthenticated: true,
          isLoading: false,
        }));
        scheduleRefresh(tokens.expiresIn);
        mintInternalSession(tokens.accessToken);
      },
      clear: () => {
        cancelScheduledRefresh();
        set({
          accessToken: null,
          idToken: null,
          refreshToken: null,
          actor: null,
          givenName: null,
          displayName: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'bata.auth.session',
      storage: createJSONStorage(safeSessionStorage),
      // Only the refresh token crosses a reload — see the module doc-comment. Everything
      // else (access token, id token, actor, the booleans) is re-derived from it.
      partialize: (state) => ({ refreshToken: state.refreshToken }),
      // Deferred with `queueMicrotask`: this callback can fire SYNCHRONOUSLY, during the
      // `create()` call itself, when storage is synchronous (sessionStorage always is) and
      // has nothing stored for this key yet — zustand's persist middleware only defers to a
      // microtask when the underlying read is a real Promise (`middleware.mjs`'s
      // `toThenable`), which a plain `sessionStorage.getItem` miss never is. Reached that way,
      // `useAuthSessionStore` below is still in its temporal dead zone (the `const` assignment
      // this callback is nested inside hasn't returned yet), so referencing it throws
      // `ReferenceError: Cannot access 'useAuthSessionStore' before initialization` --
      // caught and discarded by zustand's own `.catch()`, silently, with no visible error.
      // The visible symptom was `LoginGate`'s `Skeleton` stuck forever: `isLoading` starts
      // `true` and this callback is the only path that ever sets it to `false` on a build
      // with no stored session. `queueMicrotask` pushes the whole body past the end of the
      // current synchronous job, by which point the `const` has finished initializing.
      onRehydrateStorage: () => (state) => {
        queueMicrotask(() => {
          if (!state?.refreshToken) {
            useAuthSessionStore.getState().setLoading(false);
            return;
          }
          void runSilentRefresh();
        });
      },
    },
  ),
);

// ── the public surface ───────────────────────────────────────────────────────

/** Reactive read of the session. A shallow-compared object selector (`useShallow`)
 *  rather than three separate `useAuthSessionStore(s => s.x)` calls, so callers get one
 *  hook and one destructurable value — this is the shape the other two agents are typing
 *  against and it should read like a plain hook, not an implementation detail of zustand. */
/** Set ONLY by `playwright.config.ts`'s `webServer.command` (`VITE_E2E_BYPASS_AUTH=true npm
 *  run build`), never by the real release build (`bata-ui`'s `release.yml` has no such env
 *  var). Every e2e spec except `e2e/journeys/critical-journeys.spec.ts`'s "log in" case
 *  predates this module and asserts on screen content (axe, compact-density, pseudo-
 *  expansion) that has nothing to do with identity — G10 never made those depend on a live
 *  Keycloak, and gating the whole shell behind `LoginGate` did that as a side effect this
 *  bypass undoes for exactly the build e2e runs against. The "log in" journey itself stays
 *  `test.skip`-ped on its existing `NEEDS_AUTH` precondition (a real IdP and a seeded
 *  principal), which this bypass deliberately does not fake. */
const E2E_BYPASS_AUTH = import.meta.env['VITE_E2E_BYPASS_AUTH'] === 'true';

// No given name, on purpose: the fixture is a LOGIN, and the e2e suite then pins that a
// login is never set as a greeting (audit v5 N-07).
const E2E_SESSION: AuthSession = {
  actor: 'e2e-fixture', givenName: null, displayName: null, isAuthenticated: true, isLoading: false,
};

export function useAuthSession(): AuthSession {
  const real = useAuthSessionStore(useShallow((s) => ({
    actor: s.actor,
    givenName: s.givenName,
    displayName: s.displayName,
    isAuthenticated: s.isAuthenticated,
    isLoading: s.isLoading,
  })));
  return E2E_BYPASS_AUTH ? E2E_SESSION : real;
}

// ── identity, as the token carries it — the Profile screen ─────────────────────────

/**
 * Who the signed-in planner is, as Keycloak put it in the access token: the login, the name,
 * the e-mail and whether Keycloak has verified it, and the realm roles. For DISPLAY on the
 * Profile screen only — what the platform enforces is whatever the bearer token resolves to
 * server-side (G3.2), exactly as `decodeJwtClaims` says of the display name.
 *
 * Kept apart from `AuthSession`, whose shape other screens type against: nothing but the
 * Profile screen needs an e-mail address or a role list, and a field on `AuthSession` would
 * put personal data in every component that asks who is signed in.
 */
export interface IdentityClaims {
  readonly username: string | null;
  readonly name: string | null;
  readonly email: string | null;
  /** `null` when the token does not say — not the same as `false`, "Keycloak says unverified". */
  readonly emailVerified: boolean | null;
  /** The realm's roles, without Keycloak's own bookkeeping roles. In the token's order. */
  readonly roles: readonly string[];
}

/**
 * Keycloak's own roles, present on every user and meaning nothing about planning. Exported for
 * the User Management screen (ADR 9014), which lists the roles an administrator may assign and
 * must hide exactly these — the same rule the Profile applies to the signed-in planner.
 */
export function isKeycloakBookkeepingRole(role: string): boolean {
  return role === 'offline_access' || role === 'uma_authorization' || role.startsWith('default-roles-');
}

const NO_IDENTITY: IdentityClaims = { username: null, name: null, email: null, emailVerified: null, roles: [] };

/**
 * A JWT's payload as UTF-8 JSON, unverified — for the reasons `decodeJwtClaims` gives — or `null`
 * when it cannot be read. Shared by the Profile's identity claims and the access grants the
 * router and the User Management screen read (`./grants.ts`, ADR 9014).
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return typeof claims === 'object' && claims !== null ? claims as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

export function readIdentityClaims(token: string): IdentityClaims {
  const claims = decodeJwtPayload(token);
  if (claims === null) return NO_IDENTITY;
  const realmAccess = claims['realm_access'];
  const rawRoles = typeof realmAccess === 'object' && realmAccess !== null
    ? (realmAccess as Record<string, unknown>)['roles']
    : undefined;
  const roles = Array.isArray(rawRoles)
    ? rawRoles.filter((role): role is string => typeof role === 'string' && !isKeycloakBookkeepingRole(role))
    : [];
  const verified = claims['email_verified'];
  return {
    username: textClaim(claims['preferred_username']),
    name: textClaim(claims['name']),
    email: textClaim(claims['email']),
    emailVerified: typeof verified === 'boolean' ? verified : null,
    roles,
  };
}

const E2E_IDENTITY: IdentityClaims = { ...NO_IDENTITY, username: 'e2e-fixture' };

/** The signed-in planner's identity claims, or `null` with no session. */
export function useIdentityClaims(): IdentityClaims | null {
  const token = useAuthSessionStore((s) => s.accessToken);
  if (E2E_BYPASS_AUTH) return E2E_IDENTITY;
  return token === null ? null : readIdentityClaims(token);
}

/** Synchronous getter for `src/api/http.ts`'s transport, which builds headers
 *  synchronously per call and has no way to await a hook. Returns `null` rather than
 *  throwing when unauthenticated — an anonymous request is a normal state for this
 *  build (most of the pinned client's 91 operations predate identity entirely), not a
 *  programmer error. */
export function getAccessToken(): string | null {
  if (E2E_BYPASS_AUTH) return 'e2e-fixture-token';
  return useAuthSessionStore.getState().accessToken;
}

/** Starts the redirect login flow: generates a fresh PKCE pair and `state`, stashes them
 *  plus the current path (so the callback can return the planner where they were), and
 *  navigates to Keycloak. Returns `void`, not a promise — a redirect is the last thing
 *  this tab does, and a caller awaiting it would be waiting on a navigation that already
 *  happened. */
export function login(): void {
  startAuthorization();
}

/**
 * Sends the signed-in planner to Keycloak's own "update password" page and back to where they
 * were — the application-initiated action `UPDATE_PASSWORD` (`KeycloakAction` in `./oidc.ts`).
 *
 * It is `login()` with one extra parameter, on purpose: the same PKCE handshake, the same
 * `/auth/callback`, the same return path. Keycloak finishes the action by issuing a fresh
 * authorization code, so the callback that completes a sign-in completes this too and the tab
 * leaves with a new token pair rather than a stale one. A planner who cancels on Keycloak's page
 * comes back the same way (`kc_action_status=cancelled`), still signed in. The password itself
 * is typed only into Keycloak, never into this application.
 *
 * Under `VITE_E2E_BYPASS_AUTH` there is no identity to change a password for, and following
 * the configured issuer would leave for another environment's Keycloak — the trap `logout()`
 * below documents — so it does nothing.
 */
export function changePassword(): void {
  if (E2E_BYPASS_AUTH) return;
  startAuthorization('UPDATE_PASSWORD');
}

/**
 * Sends the signed-in planner to Keycloak's own form for their NAME — first and last — and back:
 * the application-initiated action `UPDATE_PROFILE`, the same round trip as `changePassword()`.
 * Personal data live in Keycloak (the reset-password e-mail reads them there), so they are
 * edited there and never typed into this application or the platform. The e-mail address is
 * read-only on that form (contract C5): it changes through `changeEmail()`.
 * A no-op under `VITE_E2E_BYPASS_AUTH`, for the reason `changePassword()` gives.
 */
export function editProfile(): void {
  if (E2E_BYPASS_AUTH) return;
  startAuthorization('UPDATE_PROFILE');
}

/**
 * Sends the signed-in planner to Keycloak's form for a new e-mail address — or a first one — and
 * back: `UPDATE_EMAIL` (contract C5, Keycloak's `update-email` feature). Keycloak mails a link
 * to the NEW address, and the address changes only when that link is opened; until then the
 * account, the token and every reset keep the old one. So a `success` from this action means
 * the request was accepted, never that the address changed — the callback says exactly that.
 */
export function changeEmail(): void {
  if (E2E_BYPASS_AUTH) return;
  startAuthorization('UPDATE_EMAIL');
}

/**
 * The application-initiated action the pending round trip started, or `null` for a sign-in.
 * Read by the callback BEFORE `handleAuthCallback`, which erases the handshake.
 */
export function startedKeycloakAction(): KeycloakAction | null {
  if (typeof window === 'undefined') return null;
  return asKeycloakAction(window.sessionStorage.getItem(HANDSHAKE_ACTION_KEY));
}

function startAuthorization(kcAction?: KeycloakAction): void {
  const path = `${window.location.pathname}${window.location.search}`;
  void (async () => {
    const verifier = generateCodeVerifier();
    // `state` needs the same properties as a code verifier (unguessable, single-use) and
    // none it doesn't have, so it reuses the same generator rather than inventing a
    // second "random string" primitive.
    const state = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    window.sessionStorage.setItem(HANDSHAKE_VERIFIER_KEY, verifier);
    window.sessionStorage.setItem(HANDSHAKE_STATE_KEY, state);
    window.sessionStorage.setItem(HANDSHAKE_PATH_KEY, path);
    if (kcAction === undefined) window.sessionStorage.removeItem(HANDSHAKE_ACTION_KEY);
    else window.sessionStorage.setItem(HANDSHAKE_ACTION_KEY, kcAction);
    window.location.assign(buildAuthorizeUrl(state, challenge, redirectUri(), kcAction));
  })();
}

/** Ends the session locally and at Keycloak. Local state is cleared before the redirect
 *  fires, not after, so a slow network to Keycloak never leaves stale tokens sitting in
 *  `sessionStorage` behind a pending navigation.
 *
 *  Under `VITE_E2E_BYPASS_AUTH` there is no session to end. `useAuthSession` and
 *  `getAccessToken` both honour that flag and this did not, so the control still navigated
 *  to the CONFIGURED issuer — in a local run, the production Keycloak, with a
 *  `post_logout_redirect_uri` of `http://127.0.0.1:…` that no client has registered. The
 *  planner left the app and landed on an error page belonging to another environment. The
 *  bypass says identity does not exist in this build; ending it cannot mean leaving. */
export function logout(): void {
  if (E2E_BYPASS_AUTH) {
    useAuthSessionStore.getState().clear();
    window.location.assign(`${window.location.origin}/`);
    return;
  }
  const idToken = useAuthSessionStore.getState().idToken;
  useAuthSessionStore.getState().clear();
  window.location.assign(buildEndSessionUrl(idToken, `${window.location.origin}/`));
}

/** Completes the redirect login flow from `/auth/callback`: reads `code`/`state` off
 *  `window.location.search`, verifies `state` against what `login()` stashed (rejecting a
 *  callback that doesn't match rather than trusting the URL — a mismatched or replayed
 *  `state` is exactly what the check exists to catch, per RFC 6749 §10.12), exchanges the
 *  code, and stores the resulting session. Resolves to the pre-login path so the caller
 *  can navigate the planner back to where they started rather than stranding them on
 *  `/auth/callback`. */
export async function handleAuthCallback(): Promise<string> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');

  const expectedState = window.sessionStorage.getItem(HANDSHAKE_STATE_KEY);
  const verifier = window.sessionStorage.getItem(HANDSHAKE_VERIFIER_KEY);
  const path = window.sessionStorage.getItem(HANDSHAKE_PATH_KEY) ?? '/';
  window.sessionStorage.removeItem(HANDSHAKE_STATE_KEY);
  window.sessionStorage.removeItem(HANDSHAKE_VERIFIER_KEY);
  window.sessionStorage.removeItem(HANDSHAKE_PATH_KEY);
  window.sessionStorage.removeItem(HANDSHAKE_ACTION_KEY);

  if (!code || !state || !verifier || !expectedState || state !== expectedState) {
    throw new Error(
      'OIDC callback rejected: missing code/state or state did not match the login '
      + 'attempt that started it. Never completes a login silently on a mismatch.',
    );
  }

  useAuthSessionStore.getState().setLoading(true);
  const tokens = await exchangeCodeForTokens(code, verifier, redirectUri());
  useAuthSessionStore.getState().applyTokens(tokens, tokens.idToken);
  return path;
}
